/**
 * Loads all spaces and instances, enriches them with content counts, and
 * derives the space cards used by Scene 0.
 *
 * Card shape:
 *   {
 *     key, id, name, desc,
 *     pakete, artefakte,
 *     stages: [{ code, name, inst, instanceId, glyph, stageOrder }],
 *     source, qas, prod, sourceInstance, qasInstance, prodInstance,
 *     werkeList: [{key, name, kennung}],
 *     usable, reason,
 *   }
 *
 * Stage order comes from utils/stages.js (DEV, QAS, PRE-PROD, PROD). A space
 * can use two, three or all four stages; missing ones are skipped. A chain is
 * usable once it has a source stage and at least one later stage.
 */

import { useEffect, useState } from "react";
import { api } from "../../../utils/helpers";
import { loadAllInstances, parseInstanceWerke } from "../../../utils/transport-shared";
import { STAGES, naechsteStufe, stageOrderOf } from "../../../utils/stages";

const GLYPH_BY_STAGE = { dev: "ic-stage-dev", qas: "ic-stage-qas", preprod: "ic-stage-int", prod: "ic-stage-prod" };

function stageName(key) {
  const map = {
    dev: { de: "Entwicklung", en: "Development" },
    qas: { de: "Qualitätssicherung", en: "Quality Assurance" },
    preprod: { de: "Vorproduktion", en: "Pre-Production" },
    prod: { de: "Produktion", en: "Production" },
  };
  return map[key] || null;
}

// Deliberately keyed on the stage name, never on stageOrder: stageOrder alone
// is ambiguous once PRE-PROD sits between QAS and PROD, so comparing against
// a fixed number would misclassify a PRE-PROD instance as production.
function stageKeyOf(instance) {
  const s = String(instance?.stage || "").toLowerCase();
  return STAGES.some(x => x.key === s) ? s : null;
}

async function loadContentCountsSafe(instanceId) {
  try {
    const r = await api(`/api/instances/${instanceId}/content-counts`);
    return { packages: r?.packages ?? null, artifacts: r?.artifacts ?? null };
  } catch {
    return { packages: null, artifacts: null };
  }
}

export function useSpaceLoader(lang) {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    loadAllInstances(api, { includeAll: true })
      .then(async (all) => {
        if (!mounted) return;
        const bySpace = new Map();
        for (const inst of all || []) {
          const key = inst.spaceId || inst.spaceName || "nospace";
          if (!bySpace.has(key)) bySpace.set(key, { spaceId: inst.spaceId, spaceName: inst.spaceName || "Ohne Space", insts: [] });
          bySpace.get(key).insts.push(inst);
        }
        const raw = [...bySpace.values()];
        // Fetch content counts for only the first instance of each space.
        const cards = await Promise.all(raw.map(async (sp) => {
          const tagged = sp.insts
            .map((i) => ({ inst: i, key: stageKeyOf(i) }))
            .filter((x) => x.key);
          tagged.sort((a, b) => (stageOrderOf(a.key) ?? 9) - (stageOrderOf(b.key) ?? 9));
          const vorhandene = tagged.map((x) => x.key);
          const dev = tagged.find((x) => x.key === "dev")?.inst || null;
          const prod = tagged.find((x) => x.key === "prod")?.inst || null;
          // The source is the first stage present, the target the next one
          // after it; this is no longer hardcoded to DEV and QAS. A space
          // with DEV + PRE-PROD + PROD transports from DEV to PRE-PROD.
          const quelleKey = vorhandene[0] || null;
          const zielKey = quelleKey ? naechsteStufe(vorhandene, quelleKey) : null;
          const quelle = quelleKey ? tagged.find((x) => x.key === quelleKey)?.inst || null : null;
          const ziel = zielKey ? tagged.find((x) => x.key === zielKey)?.inst || null : null;
          const stages = tagged.map((x) => {
            const nm = stageName(x.key);
            return {
              code: STAGES.find((s) => s.key === x.key)?.label || x.key.toUpperCase(),
              stageKey: x.key,
              name: nm ? (nm[lang || "de"] || nm.de) : x.inst.name,
              inst: x.inst.name,
              instanceId: x.inst.id,
              glyph: GLYPH_BY_STAGE[x.key] || "ic-stage-dev",
              stageOrder: x.inst.stageOrder,
            };
          });
          // Plants are attached to the target instance, no longer hardcoded to QAS.
          const werkeList = ziel ? parseInstanceWerke(ziel).map((w) => ({ key: w.kennung, name: w.name, kennung: w.kennung })) : [];
          // Usable as soon as two consecutive stages exist. Requiring DEV and
          // QAS specifically would block a space with DEV + PROD even though
          // it can transport.
          const usable = !!(quelle && ziel);
          // reason is an i18n key, resolved in Scene0 via t().
          const reason = !quelle ? "s0.noStage" : !ziel ? "s0.noTarget" : null;
          // Aggregate content counts.
          const counts = await Promise.all(sp.insts.slice(0, 3).map((i) => loadContentCountsSafe(i.id)));
          const paketeAll = counts.map((c) => c.packages).filter((n) => typeof n === "number");
          const artefakteAll = counts.map((c) => c.artifacts).filter((n) => typeof n === "number");
          const pakete = paketeAll.length ? Math.max(...paketeAll) : null;
          const artefakte = artefakteAll.length ? Math.max(...artefakteAll) : null;
          return {
            key: sp.spaceId || sp.spaceName,
            id: sp.spaceId,
            name: sp.spaceName,
            desc: null,
            pakete,
            artefakte,
            stages,
            sourceInstance: quelle,
            // qasInstance keeps its name for compatibility but holds the next
            // stage in the chain; for a space without QAS that may be the
            // PRE-PROD instance.
            qasInstance: ziel,
            prodInstance: prod,
            devInstance: dev,
            stufen: vorhandene,
            quellStufe: quelleKey,
            zielStufe: zielKey,
            werkeList,
            usable,
            reason,
          };
        }));
        cards.sort((a, b) => a.name.localeCompare(b.name));
        setSpaces(cards);
        setLoading(false);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e?.message || "Instanzen konnten nicht geladen werden");
        setLoading(false);
      });
    return () => { mounted = false; };
  }, [lang]);

  return { spaces, loading, error, reload: () => setLoading(true) };
}
